<template>
  <div class="product-card">
    <div class="product-image" @click="$emit('view-detail', product)">
      <img :src="product.image" :alt="product.indicator" />
      
      <!-- 标签 -->
      <div class="discount-badge">
        {{ product.proNum }}
      </div>
      
      <!-- 库存状态 -->
      <div v-if="product.stock <= 0" class="out-of-stock">
        {{ product.spec }}
      </div>
      
      <!-- 快速操作 -->
      <div class="quick-actions">
        <el-button 
          circle 
          size="small"
          @click.stop="$emit('quick-view', product)"
        >
          <el-icon><View /></el-icon>
        </el-button>
        <el-button 
          circle 
          size="small"
          @click.stop="$emit('add-to-wishlist', product)"
        >
          <el-icon><Star /></el-icon>
        </el-button>
      </div>
    </div>
    
    <div class="product-info">
      <!-- 分类标签 -->
      <div class="product-category">
        {{ product.category }}
      </div>
      
      <!-- 产品名称 -->
      <h3 class="product-name" @click="$emit('view-detail', product)">
        {{ product.indicator }}
      </h3>
      
      <!-- 产品货号 -->
      <div class="product-code">
        货号: {{ product.proNum }}
      </div>
      
      <!-- 价格 -->
      <div class="product-price" v-if="product.category === 'SH'">
        <span class="current-price">¥{{ formatPrice(product.price) }}</span>
        <span v-if="product.originalPrice" class="original-price">
          ¥{{ formatPrice(product.originalPrice) }}
        </span>
      </div>
      <div class="product-price" v-else>
        <span class="current-price"> {{ product.price }}</span>
        <span v-if="product.originalPrice" class="original-price">
          {{ product.originalPrice }}
        </span>
      </div>
      
      <!-- 评分和销量 -->
      <div class="product-meta">
        <div class="rating">
          <el-rate 
            v-model="rate" 
            disabled 
            :max="5" 
            :score-template="''"
            size="small"
          />
          <span class="rating-text">5</span>
        </div>
        <div class="sales">
          {{product.method}}
           <!-- {{ product.sales || 0 }} -->
        </div>
      </div>
      
      <!-- 操作按钮 -->
      <!-- <div class="product-actions">
        <el-button 
          type="primary" 
          size="small" 
          :disabled="product.stock <= 0"
          @click="$emit('add-to-cart', product)"
        >
          <el-icon><ShoppingCart /></el-icon>
          {{ product.stock <= 0 ? '已售罄' : '加入购物车' }}
        </el-button>
      </div> -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits,ref } from 'vue'
import type { Product } from '@/types/product'

interface Props {
  product: Product
}

interface Emits {
  (e: 'add-to-cart', product: Product): void
  (e: 'quick-view', product: Product): void
  (e: 'add-to-wishlist', product: Product): void
  (e: 'view-detail', product: Product): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const rate = ref(5)
const formatPrice = (price: number): string => {
  return price.toLocaleString('zh-CN')
}
</script>

<style scoped lang="scss">
.product-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: var(--el-box-shadow-light);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--el-box-shadow);
    
    .quick-actions {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

.product-image {
  position: relative;
  height: 200px;
  overflow: hidden;
  cursor: pointer;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  .discount-badge {
    position: absolute;
    top: 12px;
    left: 12px;
    background: var(--el-color-danger);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    z-index: 1;
  }
  
  .out-of-stock {
    position: absolute;
    top: 12px;
    right: 12px;
    background: var(--el-text-color-secondary);
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    z-index: 1;
  }
  
  .quick-actions {
    position: absolute;
    top: 12px;
    right: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s ease;
    
    .el-button {
      background: rgba(255, 255, 255, 0.9);
      
      &:hover {
        background: white;
      }
    }
  }
}

.product-info {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.product-category {
  font-size: 12px;
  color: var(--el-color-primary);
  margin-bottom: 8px;
  font-weight: 500;
}

.product-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--el-text-color-primary);
  margin-bottom: 8px;
  line-height: 1.4;
  cursor: pointer;
  transition: color 0.2s;
  
  &:hover {
    color: var(--el-color-primary);
  }
}

.product-code {
  font-size: 12px;
  color: var(--el-text-color-secondary);
  margin-bottom: 12px;
}

.product-price {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  
  .current-price {
    font-size: 20px;
    font-weight: bold;
    color: var(--el-color-danger);
  }
  
  .original-price {
    font-size: 14px;
    color: var(--el-text-color-secondary);
    text-decoration: line-through;
  }
}

.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-top: 12px;
  border-top: 1px solid var(--el-border-color);
  
  .rating {
    display: flex;
    align-items: center;
    gap: 4px;
    
    :deep(.el-rate__icon) {
      font-size: 14px;
    }
    
    .rating-text {
      font-size: 12px;
      color: var(--el-text-color-secondary);
    }
  }
  
  .sales {
    font-size: 12px;
    color: var(--el-text-color-secondary);
  }
}

.product-actions {
  margin-top: auto;
  
  .el-button {
    width: 100%;
    justify-content: center;
  }
}
</style>